/**
 * Store-wide quantity rules (5-Qty / 10-Qty tags).
 * Central utility — all surfaces should go through CLKQuantityRules.
 */
(function (window, document) {
  'use strict';

  var SELECTOR_INPUT = '[data-quantity-input], .quantity-selector__input, .element-quantity-selector__input';
  var SELECTOR_ROOT = '[data-quantity-selector], quantity-selector, .element-quantity-selector';
  var BOUND_ATTR = 'data-qty-rules-bound';

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function getRuleFromTags(tags) {
    var list = Array.isArray(tags) ? tags : String(tags || '').split(',');
    var normalized = list.map(function (t) {
      return String(t).trim();
    });

    // 5-Qty wins over 10-Qty
    if (normalized.indexOf('5-Qty') !== -1) {
      return { min: 5, step: 5, rule: '5-Qty' };
    }
    if (normalized.indexOf('10-Qty') !== -1) {
      return { min: 10, step: 10, rule: '10-Qty' };
    }
    return { min: 1, step: 1, rule: 'default' };
  }

  function getRuleFromEl(el) {
    if (!el) {
      return { min: 1, step: 1, rule: 'default' };
    }

    var source =
      el.matches && (el.matches(SELECTOR_INPUT) || el.hasAttribute('data-qty-min'))
        ? el
        : el.querySelector
          ? el.querySelector('[data-qty-min], ' + SELECTOR_INPUT)
          : null;

    if (!source) {
      source = el.closest ? el.closest('[data-qty-min]') : null;
    }

    if (!source) {
      return { min: 1, step: 1, rule: 'default' };
    }

    var min = toInt(
      source.getAttribute('data-qty-min') ||
        source.getAttribute('min') ||
        (source.dataset && source.dataset.qtyMin),
      1
    );
    var step = toInt(
      source.getAttribute('data-qty-step') ||
        source.getAttribute('data-increment-value') ||
        source.getAttribute('step') ||
        (source.dataset && source.dataset.qtyStep),
      1
    );

    if (min < 1) min = 1;
    if (step < 1) step = 1;

    return {
      min: min,
      step: step,
      rule: source.getAttribute('data-qty-rule') || 'custom'
    };
  }

  /**
   * Snap to nearest valid quantity: multiples of step, >= min.
   * Equidistant values round up.
   */
  function normalize(value, min, step) {
    min = Math.max(1, toInt(min, 1));
    step = Math.max(1, toInt(step, 1));

    if (value === '' || value === null || value === undefined || isNaN(parseInt(value, 10))) {
      return min;
    }

    var qty = toInt(value, min);
    if (qty < min) {
      return min;
    }

    var lower = Math.floor(qty / step) * step;
    var upper = lower + step;

    if (lower < min) {
      return min;
    }

    if (qty - lower < upper - qty) {
      return lower;
    }

    return upper;
  }

  function normalizeFromEl(el, value) {
    var rule = getRuleFromEl(el);
    var raw = value !== undefined && value !== null ? value : el && el.value;
    return normalize(raw, rule.min, rule.step);
  }

  function findQuantityInput(root) {
    if (!root) return null;
    if (root.matches && root.matches(SELECTOR_INPUT)) return root;
    return root.querySelector(SELECTOR_INPUT);
  }

  function applyToInput(input, options) {
    if (!input) return null;
    var opts = options || {};
    var rule = getRuleFromEl(input);
    var next = normalize(input.value, rule.min, rule.step);

    if (String(input.value) !== String(next)) {
      input.value = next;
      if (!opts.silent) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    input.setAttribute('min', String(rule.min));
    input.setAttribute('step', String(rule.step));
    if (!input.getAttribute('data-increment-value')) {
      input.setAttribute('data-increment-value', String(rule.step));
    }

    return next;
  }

  function bindClassicSelector(root) {
    if (!root || root.getAttribute(BOUND_ATTR) === 'classic') return;

    var input = findQuantityInput(root);
    if (!input) return;

    // Empire ProductDetails / StaticCart already bind PDP + cart selectors.
    // Only bind unbound surfaces (e.g. product cards).
    var alreadyEmpire =
      root.closest('[data-product-form]') ||
      root.closest('[data-cartitem]') ||
      root.closest('form[action*="/cart"]');

    root.setAttribute(BOUND_ATTR, alreadyEmpire ? 'validate-only' : 'classic');

    var onCommit = function () {
      applyToInput(input);
    };

    input.addEventListener('blur', onCommit);
    input.addEventListener('change', onCommit);

    if (alreadyEmpire) {
      applyToInput(input, { silent: true });
      return;
    }

    var plus = root.querySelector('[data-quantity-plus]');
    var minus = root.querySelector('[data-quantity-minus]');
    var rule = getRuleFromEl(input);

    function updateMinusState() {
      var wrapper = root.querySelector('[data-button-wrapper-minus]');
      if (!wrapper) return;
      var qty = toInt(input.value, rule.min);
      if (qty <= rule.min) {
        wrapper.classList.add('quantity-selector__button-wrapper--disabled');
      } else {
        wrapper.classList.remove('quantity-selector__button-wrapper--disabled');
      }
    }

    if (plus) {
      plus.addEventListener('click', function (evt) {
        evt.preventDefault();
        var current = normalizeFromEl(input);
        input.value = current + rule.step;
        applyToInput(input);
        updateMinusState();
      });
    }

    if (minus) {
      minus.addEventListener('click', function (evt) {
        evt.preventDefault();
        var current = normalizeFromEl(input);
        input.value = Math.max(rule.min, current - rule.step);
        applyToInput(input);
        updateMinusState();
      });
    }

    applyToInput(input, { silent: true });
    updateMinusState();
  }

  function bindElementSelector(root) {
    if (!root || root.getAttribute(BOUND_ATTR) === 'element') return;
    var input = root.querySelector('.element-quantity-selector__input');
    if (!input) return;

    root.setAttribute(BOUND_ATTR, 'element');

    var onCommit = function () {
      applyToInput(input);
    };

    input.addEventListener('blur', onCommit);
    root.addEventListener('quantity:change', function () {
      applyToInput(input, { silent: true });
    });

    applyToInput(input, { silent: true });
  }

  function bindAll(scope) {
    var root = scope || document;
    root.querySelectorAll(SELECTOR_ROOT).forEach(function (el) {
      if (el.classList.contains('element-quantity-selector')) {
        bindElementSelector(el);
      } else {
        bindClassicSelector(el);
      }
    });

    // Standalone inputs with rule attrs (edge cases)
    root.querySelectorAll(SELECTOR_INPUT).forEach(function (input) {
      if (input.closest(SELECTOR_ROOT)) return;
      if (input.getAttribute(BOUND_ATTR)) return;
      input.setAttribute(BOUND_ATTR, 'input');
      input.addEventListener('blur', function () {
        applyToInput(input);
      });
      applyToInput(input, { silent: true });
    });
  }

  function normalizeFormDataArray(formDataArray, options) {
    if (!Array.isArray(formDataArray)) return formDataArray;

    var atcButton = options && options.atcButton;
    var root =
      (atcButton && atcButton.closest('[data-product-item]')) ||
      (atcButton && atcButton.closest('form')) ||
      (atcButton && atcButton.closest('[data-product-form]')) ||
      document;

    var input = findQuantityInput(root) || (atcButton && atcButton.closest('[data-qty-min]'));
    var rule = getRuleFromEl(input || atcButton || root);

    return formDataArray.map(function (field) {
      if (!field || field.name !== 'quantity') return field;
      return {
        name: field.name,
        value: String(normalize(field.value, rule.min, rule.step))
      };
    });
  }

  function normalizeFormData(formData, formEl) {
    if (!formData || typeof formData.get !== 'function') return formData;
    var input =
      (formEl && findQuantityInput(formEl)) ||
      document.querySelector(
        'input[name="quantity"][form="' + ((formEl && formEl.id) || '') + '"], .product-form input[name="quantity"]'
      );
    var rule = getRuleFromEl(input || formEl);
    if (formData.has('quantity')) {
      formData.set('quantity', String(normalize(formData.get('quantity'), rule.min, rule.step)));
    }
    return formData;
  }

  function getOrderRulesMap() {
    var el = document.querySelector('[data-order-quantity-rules]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return {};
    }
  }

  function ruleForVariantId(variantId) {
    var map = getOrderRulesMap();
    var key = String(variantId);
    if (map[key]) {
      return {
        min: toInt(map[key].min, 1),
        step: toInt(map[key].step, 1),
        rule: map[key].rule || 'custom'
      };
    }

    var el = document.querySelector(
      '[data-variant-id="' + key + '"][data-qty-min], [data-cartitem-id="' + key + '"] [data-qty-min], [data-cartitem-id="' + key + '"] ' + SELECTOR_INPUT
    );
    return getRuleFromEl(el);
  }

  function normalizeCartAddBody(body) {
    if (!body) return body;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return normalizeFormData(body);
    }

    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        if (json && Array.isArray(json.items)) {
          json.items = json.items.map(function (item) {
            var rule = ruleForVariantId(item.id);
            item.quantity = normalize(item.quantity, rule.min, rule.step);
            return item;
          });
          return JSON.stringify(json);
        }
        if (json && json.quantity !== undefined && json.id) {
          var singleRule = ruleForVariantId(json.id);
          json.quantity = normalize(json.quantity, singleRule.min, singleRule.step);
          return JSON.stringify(json);
        }
      } catch (err) {
        // URL-encoded form body
        try {
          var params = new URLSearchParams(body);
          if (params.has('quantity')) {
            var id = params.get('id');
            var rule = id ? ruleForVariantId(id) : { min: 1, step: 1 };
            var input = document.querySelector('[data-quantity-input][name="quantity"], input[name="quantity"]');
            if (input) rule = getRuleFromEl(input);
            params.set('quantity', String(normalize(params.get('quantity'), rule.min, rule.step)));
            return params.toString();
          }
        } catch (e2) {
          /* ignore */
        }
      }
    }

    return body;
  }

  function normalizeCartChangeBody(body) {
    if (!body) return body;

    function apply(obj) {
      if (!obj || obj.quantity === undefined) return obj;
      if (toInt(obj.quantity, 0) === 0) return obj; // removals

      var rule = { min: 1, step: 1 };
      if (obj.id) {
        var line = document.querySelector('[data-cartitem-key="' + obj.id + '"], [data-cartitem-id="' + obj.id + '"]');
        if (line) {
          rule = getRuleFromEl(findQuantityInput(line) || line);
        } else {
          rule = ruleForVariantId(obj.id);
        }
      }
      obj.quantity = normalize(obj.quantity, rule.min, rule.step);
      return obj;
    }

    if (typeof body === 'string') {
      try {
        return JSON.stringify(apply(JSON.parse(body)));
      } catch (err) {
        return body;
      }
    }

    if (typeof body === 'object') {
      return apply(body);
    }

    return body;
  }

  function patchFetch() {
    if (!window.fetch || window.fetch.__qtyRulesPatched) return;
    var originalFetch = window.fetch.bind(window);

    function patchedFetch(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var config = init ? Object.assign({}, init) : {};

      if (/\/cart\/add(\.js)?/i.test(url) && config.body) {
        config.body = normalizeCartAddBody(config.body);
      }
      if (/\/cart\/change(\.js)?/i.test(url) && config.body) {
        config.body = normalizeCartChangeBody(config.body);
      }

      return originalFetch(input, config);
    }

    patchedFetch.__qtyRulesPatched = true;
    window.fetch = patchedFetch;
  }

  function patchJqueryAjax() {
    if (!window.jQuery || !window.jQuery.ajax || window.jQuery.ajax.__qtyRulesPatched) return;
    var originalAjax = window.jQuery.ajax;

    window.jQuery.ajax = function (url, options) {
      var settings = options;
      var requestUrl = url;

      if (typeof url === 'object') {
        settings = url;
        requestUrl = settings.url || '';
      } else {
        settings = options || {};
      }

      if (requestUrl && /\/cart\/add(\.js)?/i.test(String(requestUrl)) && settings.data) {
        if (typeof settings.data === 'string') {
          settings.data = normalizeCartAddBody(settings.data);
        } else if (typeof settings.data === 'object' && settings.data.quantity !== undefined) {
          var rule = settings.data.id
            ? ruleForVariantId(settings.data.id)
            : getRuleFromEl(document.querySelector(SELECTOR_INPUT));
          settings.data.quantity = normalize(settings.data.quantity, rule.min, rule.step);
        }
      }

      if (typeof url === 'object') {
        return originalAjax.call(this, settings);
      }
      return originalAjax.call(this, requestUrl, settings);
    };

    window.jQuery.ajax.__qtyRulesPatched = true;
  }

  function patchAddToCartFlyout() {
    if (!window.AddToCartFlyout || window.AddToCartFlyout.__qtyRulesPatched) return false;

    var Original = window.AddToCartFlyout;

    function Patched(formData, options, callbacks) {
      var normalized = normalizeFormDataArray(formData, options || {});
      return new Original(normalized, options, callbacks);
    }

    Patched.prototype = Original.prototype;
    Object.keys(Original).forEach(function (key) {
      try {
        Patched[key] = Original[key];
      } catch (err) {
        /* ignore */
      }
    });
    Patched.__qtyRulesPatched = true;
    window.AddToCartFlyout = Patched;
    return true;
  }

  function interceptQuickBuy() {
    document.addEventListener(
      'click',
      function (evt) {
        var btn = evt.target.closest && evt.target.closest('[data-quick-buy]');
        if (!btn || btn.disabled || btn.classList.contains('disabled')) return;
        if (!window.AddToCartFlyout) return;

        var card = btn.closest('[data-product-item]') || btn.closest('.productitem') || btn.parentElement;
        var input = card ? findQuantityInput(card) : null;
        var rule = getRuleFromEl(input || btn);
        var qty = input ? normalizeFromEl(input) : rule.min;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        var settingsEl = card && card.querySelector('[data-quick-buy-settings]');
        var settings = { money_format: null, cart_redirection: false };
        if (settingsEl) {
          try {
            settings = JSON.parse(settingsEl.textContent);
          } catch (err) {
            /* ignore */
          }
        }

        var formData = [
          { name: 'id', value: btn.getAttribute('data-variant-id') },
          { name: 'quantity', value: String(qty) }
        ];

        new window.AddToCartFlyout(formData, {
          atcButton: btn,
          settings: {
            moneyFormat: settings.money_format,
            cartRedirection: settings.cart_redirection
          }
        });
      },
      true
    );
  }

  function interceptProductFormEvents() {
    document.addEventListener('product-form:submit:before', function (evt) {
      if (!evt.detail || !evt.detail.form) return;
      var formEl = evt.target && evt.target.querySelector ? evt.target.querySelector('form') : null;
      normalizeFormData(evt.detail.form, formEl || evt.target);
    });
  }

  function interceptClassicFormSubmit() {
    document.addEventListener(
      'submit',
      function (evt) {
        var form = evt.target;
        if (!form || !form.matches) return;
        if (!form.matches('[data-product-form], form[action*="/cart/add"]')) return;

        var input = findQuantityInput(form);
        if (input) applyToInput(input, { silent: true });
      },
      true
    );

    // Dynamic checkout / Buy Now reads the form quantity without a normal submit path
    document.addEventListener(
      'click',
      function (evt) {
        var payment =
          evt.target.closest &&
          evt.target.closest(
            '.shopify-payment-button button, .shopify-payment-button__button, [data-shopify-buttoncontainer] button'
          );
        if (!payment) return;

        var form =
          payment.closest('form') ||
          document.querySelector('[data-product-form]') ||
          document.querySelector('.product-form form');
        if (!form) return;

        var input = findQuantityInput(form) || document.querySelector('input[name="quantity"][form="' + form.id + '"]');
        if (input) applyToInput(input, { silent: true });
      },
      true
    );
  }

  function observeDom() {
    if (!window.MutationObserver) return;
    var timer = null;
    var observer = new MutationObserver(function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        bindAll(document);
        patchAddToCartFlyout();
        patchJqueryAjax();
      }, 100);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function syncInvalidCartQuantities() {
    var lines = document.querySelectorAll('[data-cartitem]');
    if (!lines.length) return;

    lines.forEach(function (line) {
      var input = findQuantityInput(line);
      if (!input) return;

      var rule = getRuleFromEl(input);
      var current = toInt(input.value, rule.min);
      var next = normalize(current, rule.min, rule.step);
      if (current === next) return;

      input.value = next;
      // Trigger empire cart update path
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function init() {
    patchFetch();
    patchJqueryAjax();
    patchAddToCartFlyout();
    interceptQuickBuy();
    interceptProductFormEvents();
    interceptClassicFormSubmit();
    bindAll(document);
    observeDom();

    // After empire binds cart handlers, snap any invalid line quantities
    setTimeout(syncInvalidCartQuantities, 500);

    // Empire loads async — keep trying to patch flyout briefly
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      patchAddToCartFlyout();
      patchJqueryAjax();
      if ((window.AddToCartFlyout && window.AddToCartFlyout.__qtyRulesPatched) || attempts > 40) {
        clearInterval(timer);
      }
    }, 100);
  }

  window.CLKQuantityRules = {
    getRuleFromTags: getRuleFromTags,
    getRuleFromEl: getRuleFromEl,
    normalize: normalize,
    normalizeFromEl: normalizeFromEl,
    applyToInput: applyToInput,
    normalizeFormDataArray: normalizeFormDataArray,
    normalizeFormData: normalizeFormData,
    bindAll: bindAll,
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);

/**
 * CLK access rules — GMSMX13 products / MX13 customers.
 * Opens the theme login widget (shopify-account) for guests.
 */
(function (window, document) {
  'use strict';

  function openLoginWidget() {
    var trigger =
      document.querySelector('shopify-account [slot="signed-out-avatar"] .site-header_account-link-anchor') ||
      document.querySelector('shopify-account .site-header_account-link-anchor') ||
      document.querySelector('.site-header_account-link-anchor');

    if (trigger) {
      trigger.click();
      return true;
    }

    var account = document.querySelector('shopify-account');
    if (account) {
      account.click();
      return true;
    }

    var loginUrl =
      (window.Theme && window.Theme.routes && window.Theme.routes.account_login_url) || '/account/login';
    var returnTo = window.location.pathname + window.location.search;
    window.location.href = loginUrl + (loginUrl.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + encodeURIComponent(returnTo);
    return false;
  }

  function bindGate(gate) {
    if (!gate || gate.getAttribute('data-clk-access-bound')) return;
    gate.setAttribute('data-clk-access-bound', 'true');

    var mode = gate.getAttribute('data-clk-access-mode');
    var buttons = gate.querySelectorAll('[data-clk-open-login]');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        openLoginWidget();
      });
    });

    // Guests on restricted product/collection: open login widget automatically
    if (mode === 'login') {
      setTimeout(openLoginWidget, 300);
    }
  }

  function initAccess() {
    document.querySelectorAll('[data-clk-access-gate]').forEach(bindGate);

    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-clk-open-login]');
      if (!btn) return;
      evt.preventDefault();
      openLoginWidget();
    });
  }

  window.CLKAccessRules = {
    openLoginWidget: openLoginWidget,
    init: initAccess
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccess);
  } else {
    initAccess();
  }
})(window, document);

/**
 * CLK Header — Phase 4 interactions (no theme script changes).
 * Drawer ownership, scroll lock, focus, flyout bridge, Vehicle/Machine coordination.
 */
(function (window, document) {
  'use strict';

  var DESKTOP_MQ = '(min-width: 1024px)';
  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
  var state = {
    header: null,
    drawer: null,
    panel: null,
    overlay: null,
    open: false,
    activeParent: null,
    lastFocus: null,
    flyoutTimer: null,
    scrollY: 0
  };

  function isDesktop() {
    return window.matchMedia && window.matchMedia(DESKTOP_MQ).matches;
  }

  function setMenuLabel(isOpen) {
    document.querySelectorAll('[data-clk-menu-label]').forEach(function (label) {
      label.textContent = isOpen ? 'Close' : 'Menu';
    });

    document.querySelectorAll('[data-clk-menu-toggle]').forEach(function (btn) {
      btn.classList.toggle('is-open', !!isOpen);
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function lockScroll() {
    state.scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('clk-drawer-open');
    document.body.classList.add('clk-drawer-open');
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + state.scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.documentElement.classList.remove('clk-drawer-open');
    document.body.classList.remove('clk-drawer-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, state.scrollY || 0);
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function focusDrawer() {
    if (!state.panel) return;
    var closeBtn = state.panel.querySelector('[data-clk-drawer-close], [data-mobile-nav-close]');
    var focusables = getFocusable(state.panel);
    var target = closeBtn || focusables[0] || state.panel;
    window.setTimeout(function () {
      if (target && typeof target.focus === 'function') target.focus();
    }, 50);
  }

  function restoreFocus() {
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
    state.lastFocus = null;
  }

  function clearFlyouts() {
    if (!state.drawer) return;
    if (state.flyoutTimer) {
      window.clearTimeout(state.flyoutTimer);
      state.flyoutTimer = null;
    }

    state.drawer.querySelectorAll('.navmenu-item-parent.is-flyout-active').forEach(function (item) {
      item.classList.remove('is-flyout-active');
      var trigger = item.querySelector('[data-navmenu-trigger]');
      var link = item.querySelector(':scope > .navmenu-link-parent');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (link) link.setAttribute('aria-expanded', 'false');
    });
    state.activeParent = null;
    state.drawer.classList.remove('has-flyout');
  }

  function getParentSubmenu(parent) {
    if (!parent) return null;
    return (
      parent.querySelector(':scope > .navmenu[data-navmenu-submenu]') ||
      parent.querySelector(':scope > .navmenu-submenu')
    );
  }

  function ensureFlyoutBackButton(submenu) {
    if (!submenu || submenu.querySelector('[data-clk-flyout-back]')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'clk-drawer__flyout-back';
    btn.setAttribute('data-clk-flyout-back', '');
    btn.textContent = 'Back';
    btn.addEventListener('click', function (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      clearFlyouts();
    });
    submenu.insertBefore(btn, submenu.firstChild);
  }

  function activateFlyout(parent) {
    if (!parent || !state.open) return;
    if (state.flyoutTimer) {
      window.clearTimeout(state.flyoutTimer);
      state.flyoutTimer = null;
    }
    if (state.activeParent === parent) return;

    state.drawer.querySelectorAll('.navmenu-item-parent.is-flyout-active').forEach(function (item) {
      if (item !== parent) {
        item.classList.remove('is-flyout-active');
        var t = item.querySelector('[data-navmenu-trigger]');
        var l = item.querySelector(':scope > .navmenu-link-parent');
        if (t) t.setAttribute('aria-expanded', 'false');
        if (l) l.setAttribute('aria-expanded', 'false');
      }
    });

    var submenu = getParentSubmenu(parent);
    if (submenu) ensureFlyoutBackButton(submenu);

    parent.classList.add('is-flyout-active');
    state.activeParent = parent;
    state.drawer.classList.add('has-flyout');

    var trigger = parent.querySelector('[data-navmenu-trigger]');
    var link = parent.querySelector(':scope > .navmenu-link-parent');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (link) link.setAttribute('aria-expanded', 'true');

    if (submenu && !isDesktop()) {
      var backBtn = submenu.querySelector('[data-clk-flyout-back]');
      window.setTimeout(function () {
        if (backBtn && typeof backBtn.focus === 'function') backBtn.focus();
      }, 50);
    }
  }

  function scheduleClearFlyout() {
    if (!isDesktop()) return;
    if (state.flyoutTimer) window.clearTimeout(state.flyoutTimer);
    state.flyoutTimer = window.setTimeout(function () {
      clearFlyouts();
    }, 180);
  }

  function openFirstFlyout() {
    if (!isDesktop() || !state.drawer) return;
    var firstParent = state.drawer.querySelector(
      '.clk-drawer__categories .navmenu-depth-1 > .navmenu-item-parent'
    );
    if (firstParent) activateFlyout(firstParent);
  }

  function openDrawer() {
    if (!state.drawer || !state.panel || state.open) return;

    state.lastFocus = document.activeElement;
    state.drawer.setAttribute('data-open', 'true');
    state.panel.setAttribute('data-animation', 'closed=>open');
    state.panel.setAttribute('data-animation-state', 'open');
    if (state.overlay) {
      state.overlay.setAttribute('data-animation', 'closed=>open');
      state.overlay.setAttribute('data-animation-state', 'open');
    }

    lockScroll();
    state.open = true;
    setMenuLabel(true);
    focusDrawer();
    openFirstFlyout();
  }

  function closeDrawer() {
    if (!state.drawer || !state.panel || !state.open) return;

    clearFlyouts();
    state.panel.setAttribute('data-animation', 'open=>closed');
    state.panel.setAttribute('data-animation-state', 'closed');
    if (state.overlay) {
      state.overlay.setAttribute('data-animation', 'open=>closed');
      state.overlay.setAttribute('data-animation-state', 'closed');
    }
    state.drawer.setAttribute('data-open', 'false');

    unlockScroll();
    state.open = false;
    setMenuLabel(false);
    restoreFocus();
  }

  function toggleDrawer(evt) {
    if (evt) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
    }
    if (state.open) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  function trapFocus(evt) {
    if (!state.open || evt.key !== 'Tab') return;

    var activeSubmenu =
      state.activeParent && !isDesktop() ? getParentSubmenu(state.activeParent) : null;
    var container = activeSubmenu || state.panel;
    if (!container) return;

    var focusables = getFocusable(container);
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  function bindFlyouts() {
    if (!state.drawer) return;
    var parents = state.drawer.querySelectorAll(
      '.clk-drawer__categories .navmenu-depth-1 > .navmenu-item-parent'
    );

    parents.forEach(function (parent) {
      parent.addEventListener('mouseenter', function () {
        if (isDesktop() && state.open) activateFlyout(parent);
      });

      parent.addEventListener('mouseleave', function () {
        if (isDesktop() && state.open) scheduleClearFlyout();
      });

      var submenu =
        parent.querySelector(':scope > .navmenu[data-navmenu-submenu]') ||
        parent.querySelector(':scope > .navmenu-submenu');
      if (submenu) {
        submenu.addEventListener('mouseenter', function () {
          if (state.flyoutTimer) {
            window.clearTimeout(state.flyoutTimer);
            state.flyoutTimer = null;
          }
          activateFlyout(parent);
        });
        submenu.addEventListener('mouseleave', function () {
          if (isDesktop() && state.open) scheduleClearFlyout();
        });
      }

      var trigger = parent.querySelector(':scope > [data-navmenu-trigger]');
      var link = parent.querySelector(':scope > .navmenu-link-parent');

      if (trigger) {
        trigger.addEventListener(
          'click',
          function (evt) {
            if (!state.open) return;
            evt.preventDefault();
            evt.stopImmediatePropagation();
            if (parent.classList.contains('is-flyout-active')) {
              clearFlyouts();
            } else {
              activateFlyout(parent);
            }
          },
          true
        );
      }

      if (link) {
        link.addEventListener('click', function (evt) {
          if (!state.open) return;
          // First tap opens flyout (desktop + mobile); second tap follows link
          if (!parent.classList.contains('is-flyout-active')) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            activateFlyout(parent);
          }
        }, true);
      }
    });
  }

  function closeSelectorDialogs() {
    var vehicleDialog = document.getElementById('vehicleDialog');
    var machineDialog = document.getElementById('machineDialog');
    if (vehicleDialog) vehicleDialog.classList.remove('active');
    if (machineDialog) machineDialog.classList.remove('active');
    document.querySelectorAll('.js-dialog-trigger.active, .js-machine-dialog-trigger.active').forEach(function (el) {
      el.classList.remove('active');
    });
  }

  function bindSelectorCoordination() {
    document.querySelectorAll('.js-dialog-trigger, .js-machine-dialog-trigger').forEach(function (trigger) {
      trigger.addEventListener(
        'click',
        function () {
          // Opening a selector should dismiss the menu drawer
          if (state.open) closeDrawer();

          // Only one selector dialog open at a time
          var isVehicle = trigger.classList.contains('js-dialog-trigger');
          var otherDialog = document.getElementById(isVehicle ? 'machineDialog' : 'vehicleDialog');
          var otherTriggers = document.querySelectorAll(
            isVehicle ? '.js-machine-dialog-trigger' : '.js-dialog-trigger'
          );
          if (otherDialog) otherDialog.classList.remove('active');
          otherTriggers.forEach(function (el) {
            el.classList.remove('active');
          });
        },
        true
      );
    });
  }

  function bindStickyPolish(header) {
    if (!header) return;

    var onScroll = function () {
      // While drawer locks body, ignore scroll class churn
      if (state.open) return;
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initHeader() {
    var header = document.querySelector('.clk-header');
    var drawer = document.querySelector('[data-clk-drawer]');
    if (!header || !drawer) return;

    state.header = header;
    state.drawer = drawer;
    state.panel = drawer.querySelector('[data-clk-drawer-panel]') || drawer.querySelector('[data-mobile-nav-panel]');
    state.overlay = drawer.querySelector('[data-clk-drawer-overlay]') || drawer.querySelector('[data-mobile-nav-overlay]');
    state.open = drawer.getAttribute('data-open') === 'true';
    setMenuLabel(state.open);
    bindStickyPolish(header);

    document.querySelectorAll('[data-clk-menu-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-controls', 'site-mobile-nav');
      btn.setAttribute('aria-expanded', state.open ? 'true' : 'false');
      btn.addEventListener('click', toggleDrawer, true);
    });

    // Block Empire's own menu-toggle handlers on the same control
    document.querySelectorAll('.clk-header [data-menu-toggle]').forEach(function (btn) {
      btn.addEventListener(
        'click',
        function (evt) {
          if (btn.hasAttribute('data-clk-menu-toggle')) return;
          toggleDrawer(evt);
        },
        true
      );
    });

    if (state.overlay) {
      state.overlay.addEventListener('click', function (evt) {
        evt.preventDefault();
        closeDrawer();
      });
    }

    drawer.querySelectorAll('[data-clk-drawer-close], [data-mobile-nav-close]').forEach(function (btn) {
      btn.addEventListener(
        'click',
        function (evt) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          closeDrawer();
        },
        true
      );
    });

    document.addEventListener('keydown', function (evt) {
      if (!state.open) return;
      if (evt.key === 'Escape') {
        if (state.activeParent && !isDesktop()) {
          clearFlyouts();
        } else {
          closeDrawer();
        }
        return;
      }
      trapFocus(evt);
    });

    window.addEventListener('resize', function () {
      if (!state.open) return;
      if (isDesktop() && !state.activeParent) {
        openFirstFlyout();
      }
    });

    bindFlyouts();
    bindSelectorCoordination();
  }

  window.CLKHeader = {
    init: initHeader,
    open: openDrawer,
    close: closeDrawer,
    setMenuLabel: setMenuLabel,
    closeSelectorDialogs: closeSelectorDialogs
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeader);
  } else {
    initHeader();
  }
})(window, document);
